import { IItem } from '../models/loft';
import { IQuadrant, IQuadrantConstraint, IQuadrantFtRelaxation } from '../models/assessment-framework';

export type QuadrantFtRelaxTriggerMode = 'intersection' | 'any' | 'all';

const DEFAULT_TRIGGER_PARAM = 'FT';
const DEFAULT_TRIGGER_VAL = '1';

/** Split constraint / meta values on comma (same semantics as refreshQuadrantItems). */
export function splitConstraintValues(raw: string | number | null | undefined): string[] {
  return String(raw ?? '')
    .split(',')
    .map((part) => part.trim())
    .filter(Boolean);
}

/** True when item meta matches any allowed token (after trim / numStrToBool parity). */
export function itemMetaMatchesAllowedValues(
  meta: Record<string, unknown> | undefined,
  param: string,
  allowedRaw: string | number | null | undefined,
): boolean {
  if (!meta || !param) {
    return false;
  }
  const itemVal = String(meta[param] ?? '').trim();
  const allowed = splitConstraintValues(allowedRaw);
  if (!allowed.length) {
    return false;
  }
  return allowed.some((token) => {
    if (itemVal === token) {
      return true;
    }
    // Binary param parity (0/1/false/true)
    if ((itemVal === '0' || itemVal === 'false') && (token === '0' || token === 'false')) {
      return true;
    }
    if ((itemVal === '1' || itemVal === 'true') && (token === '1' || token === 'true')) {
      return true;
    }
    return false;
  });
}

export function ensureQuadrantFtRelaxation(quadrant: IQuadrant): IQuadrantFtRelaxation {
  if (!quadrant.ftRelaxation) {
    quadrant.ftRelaxation = {};
  }
  return quadrant.ftRelaxation;
}

export function getFtRelaxTriggerParam(relax: IQuadrantFtRelaxation | undefined): string {
  const param = relax?.triggerParam?.trim();
  return param || DEFAULT_TRIGGER_PARAM;
}

export function getFtRelaxTriggerVal(relax: IQuadrantFtRelaxation | undefined): string {
  const val = relax?.triggerVal?.trim();
  return val !== undefined && val !== '' ? val : DEFAULT_TRIGGER_VAL;
}

function quadrantIntersectionDeclaresFt(
  quadrant: IQuadrant,
  relax: IQuadrantFtRelaxation,
): boolean {
  const triggerParam = getFtRelaxTriggerParam(relax);
  const triggerTokens = splitConstraintValues(getFtRelaxTriggerVal(relax));
  return (quadrant.constraints || []).some((constraint: IQuadrantConstraint) => {
    if (constraint.param !== triggerParam) {
      return false;
    }
    const declared = splitConstraintValues(constraint.val);
    return declared.some((token) => triggerTokens.includes(token));
  });
}

function testletItemsMatchFtTrigger(
  items: IItem[],
  relax: IQuadrantFtRelaxation,
  mode: 'any' | 'all',
): boolean {
  if (!items.length) {
    return false;
  }
  const triggerParam = getFtRelaxTriggerParam(relax);
  const triggerVal = getFtRelaxTriggerVal(relax);
  const predicate = (item: IItem) =>
    itemMetaMatchesAllowedValues(item.meta as Record<string, unknown>, triggerParam, triggerVal);
  return mode === 'all' ? items.every(predicate) : items.some(predicate);
}

/** Whether FT relaxation rules apply for this quadrant + testlet item set. */
export function isFtRelaxationActive(
  quadrant: IQuadrant | undefined,
  items: IItem[],
): boolean {
  const relax = quadrant?.ftRelaxation;
  if (!relax?.enabled) {
    return false;
  }
  const mode: QuadrantFtRelaxTriggerMode = relax.triggerMode || 'intersection';
  switch (mode) {
    case 'intersection':
      return quadrantIntersectionDeclaresFt(quadrant, relax);
    case 'all':
      return testletItemsMatchFtTrigger(items, relax, 'all');
    case 'any':
    default:
      return testletItemsMatchFtTrigger(items, relax, 'any');
  }
}

export function shouldSkipRelaxedIntersectionParam(
  quadrant: IQuadrant | undefined,
  items: IItem[],
  param: string,
): boolean {
  if (!isFtRelaxationActive(quadrant, items)) {
    return false;
  }
  const skipParams = quadrant?.ftRelaxation?.skipIntersectionParams || [];
  if (!skipParams.length) {
    return false;
  }
  return skipParams.some((p) => p.trim() === param);
}

export function isFlexibleNumItemsAllowed(
  quadrant: IQuadrant | undefined,
  items: IItem[],
  actualCount: number,
): boolean {
  if (!isFtRelaxationActive(quadrant, items)) {
    return false;
  }
  const relax = quadrant?.ftRelaxation;
  if (!relax?.allowFlexibleNumItems) {
    return false;
  }
  const min = relax.numItemsMin;
  const max = relax.numItemsMax;
  if (min != null && !Number.isNaN(+min) && actualCount < +min) {
    return false;
  }
  if (max != null && !Number.isNaN(+max) && actualCount > +max) {
    return false;
  }
  return true;
}

export function parseSkipIntersectionParams(raw: string | null | undefined): string[] {
  return splitConstraintValues(raw).filter(Boolean);
}

export function formatSkipIntersectionParams(params: string[] | undefined): string {
  return (params || []).join(',');
}
